import {
  AfterViewInit,
  Component,
  HostListener,
  TemplateRef,
  ViewChild,
  Inject,
} from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import {
  BroadcastService,
  NavigationService,
  Deferred,
  OperationCanceledException,
} from "../../modules/tcm-core.module";
import { fromEvent, merge, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";
import {
  MenuQuantityItemEnum,
  MenuQuantityItemViewModel,
  MenuWorkQueueSubMenuViewModel,
} from "../../models/menu";
import { EntityPermissionNode, RoleAccessSettings } from "../../models/roles";
import { AuthService, PermissionsService, UserClinicService } from "../../services";
import { MenuQuantitiesService } from "../../services/menu-quantities.service";
import { MenuChildNode, MenuMainNode } from "./configurations/nodes";
import { MenuConfiguration } from "./menu-configuration";
import { Task } from "../../helpers/task";
import {
  SYSTEM_PREFERENCES,
  SystemPreferences,
} from "../../common/system-preferences";
import { DefaultPageEnum, ErpSystemEnum } from "../../models/enums";
import { MenuCustomKeys } from "./menu-custom-keys";
import { TcmImagesSystemService } from "../../internals/tcm-images-system-service";
import { entityKeyResolver } from "../../actions/work-queues/work-queues-routing.module";
import { environment } from "../../../environments/environment";

@Component({
    selector: "menu",
    templateUrl: "./menu.component.html",
    styleUrls: ["./menu.component.scss"],
    standalone: false
})
export class MenuComponent implements AfterViewInit {
  @ViewChild("dashboardIconTemplate", { static: true })
  dashboardIconTemplate: TemplateRef<any>;
  @ViewChild("workQueuesIconTemplate", { static: true })
  workQueuesIconTemplate: TemplateRef<any>;
  @ViewChild("schedulingIconTemplate", { static: true })
  schedulingIconTemplate: TemplateRef<any>;
  @ViewChild("patientsIconTemplate", { static: true })
  patientsIconTemplate: TemplateRef<any>;
  @ViewChild("salesIconTemplate", { static: true })
  salesIconTemplate: TemplateRef<any>;
  @ViewChild("claimsIconTemplate", { static: true })
  claimsIconTemplate: TemplateRef<any>;
  @ViewChild("paymentTerminalIconTemplate", { static: true })
  paymentTerminalIconTemplate: TemplateRef<any>;
  @ViewChild("billingIconTemplate", { static: true })
  billingIconTemplate: TemplateRef<any>;
  @ViewChild("catalogIconTemplate", { static: true })
  catalogIconTemplate: TemplateRef<any>;
  @ViewChild("inventoryIconTemplate", { static: true })
  inventoryIconTemplate: TemplateRef<any>;
  @ViewChild("marketingIconTemplate", { static: true })
  marketingIconTemplate: TemplateRef<any>;
  @ViewChild("outreachIconTemplate", { static: true })
  outreachIconTemplate: TemplateRef<any>;
  @ViewChild("reportsIconTemplate", { static: true })
  reportsIconTemplate: TemplateRef<any>;
  @ViewChild("adminIconTemplate", { static: true })
  adminIconTemplate: TemplateRef<any>;

  readonly version: string;
  readonly build: string;
  menuQuantities: any = {};
  workQueueMenuQuantities: any = {};
  hasMenuPermissions: boolean = false;
  that: MenuComponent;
  menuNodes: MenuMainNode[];
  openedMainNode: MenuMainNode;
  openedFirstMainNode: MenuMainNode;
  isOpenedMainNodeCollapsed: boolean;
  isSidebarVisible: boolean;
  isSidebarOpen: boolean;
  isSidebarPinned: boolean;
  isLogoError: boolean = false;

  workQueue = MenuQuantityItemEnum.WorkQueue;
  workQueueAreaPermissionKeyName = 'workQueue';
  workQueueKeyTitle = 'Menu.WorkQueues.Name';
  rootPathToExclude= ['/patients','/sales-orders'];

  private updateMenuQuantitiesSubscription: Subscription;
  private updateWorkQueueMenuQuantitiesSubscription: Subscription;
  private updateWorkQueueSubMenuSubscription: Subscription;
  private routerNavigationSubscription: Subscription;
  private mobileShowMenuSubscription: Subscription;
  private permissionsSubscription: Subscription;

  constructor(
    protected menuQuantitiesService: MenuQuantitiesService,
    protected broadcastService: BroadcastService,
    @Inject(SYSTEM_PREFERENCES) private sysPref: SystemPreferences,
    private navigationService: NavigationService,
    private permissionService: PermissionsService,
    public imagesService: TcmImagesSystemService,
    private router: Router,
    private userClinicService: UserClinicService,
    private authService : AuthService
  ) {
    this.that = this;
    this.version = environment.VERSION;
    if (isNaN(parseInt(environment.BUILD))) {
      this.build = environment.BUILD;
    } else {
      this.build = parseInt(environment.BUILD).toString();
    }

    this.menuNodes = MenuConfiguration.getNodes();

    this.onNavigationEnd();
    this.updateMenuQuantitiesSubscription =
      this.menuQuantitiesService.quantifiers$.subscribe((args) => {
        this.setMenuQuantities(args);
      });

      this.updateWorkQueueMenuQuantitiesSubscription =
      this.menuQuantitiesService.workQueueQuantifiers$.subscribe((args) => {
        this.setWorkQueueMenuQuantities(args);
      });

    this.updateWorkQueueSubMenuSubscription =
      this.menuQuantitiesService.workQueueSubMenu$.subscribe((args) => {
        this.updateWorkQueueSubMenu(args);
      });

    this.routerNavigationSubscription = this.router.events
      .pipe(
        filter((x) => x instanceof NavigationEnd),
        tap((x) => {
          this.onNavigationEnd()
        })
      )
      .subscribe();
  }

  updateWorkQueueSubMenu(additionalNodes: MenuWorkQueueSubMenuViewModel[]) {
    var children: MenuChildNode[] = [];

    additionalNodes.forEach(element => {
      const node: MenuChildNode = {
        title: element.workQueueName,
        abbreviation: element.workQueueName,
        link: '/work-queues/' + (element.workQueueName != 'Unassigned' ? element.id : 'unassigned'),
        quantityType: MenuQuantityItemEnum.WorkQueue,
        quantity: element.uncompletedNotesCount,
        isVisible:true,
        entityPermissionKey:entityKeyResolver('workQueues'),
        id: element.id
      };
      children.push(node);
    });

    const parentNode = this.menuNodes.find((x) => x.rootPath.startsWith("/work-queues"));
    parentNode.children = [];
    parentNode.children = children;
  }

  ngOnDestroy() {
    this.updateMenuQuantitiesSubscription.unsubscribe();
    this.mobileShowMenuSubscription.unsubscribe();
    this.routerNavigationSubscription.unsubscribe();
    this.updateWorkQueueSubMenuSubscription.unsubscribe();

    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe();
    }
  }

  async ngAfterViewInit() {

      var children: MenuChildNode[] = [];

      const node:MenuChildNode={
        title: 'Unassigned',
          abbreviation: 'Unassigned',
          link: '/work-queues/unassigned',
          ignorePermissions: true,
          quantityType: MenuQuantityItemEnum.WorkQueue,
          quantity: 0,
      }
      children.push(node);

      const parentNode = this.menuNodes.find((x) => x.rootPath.startsWith("/work-queues"));
      parentNode.children = children;

    await Task.delay(0);

    const events = ["click", "touchstart"].map((x) => fromEvent(document, x));
    this.mobileShowMenuSubscription = merge(...events)
      .pipe(
        filter(
          (x) =>
            x.target &&
            (x.target as HTMLElement).getAttribute("data-toggle") == "sidebar"
        )
      )
      .subscribe((event) => {
        event.preventDefault();
        this.toggleMobileSidebar();
      });

    this.permissionsSubscription =
      this.permissionService.permissions$.subscribe((x) => {
        this.applyPermissions(x);
      }
      );

    this.toggleMenuPin();
  }

  toggleMobileSidebar() {
    if (document.body.classList.contains("sidebar-open")) {
      this.hideMobileSidebar();
    } else {
      this.showMobileSidebar();
    }
  }

  @HostListener("window:resize")
  toggleMenuPin() {
    if (
      (window.innerWidth < 1200 && this.isSidebarPinned) ||
      (window.innerWidth >= 1200 && !this.isSidebarPinned)
    ) {
      this.onMenuToggleButtonClicked();
      this.isSidebarOpen = this.isSidebarPinned;
    }

    if (window.innerWidth <= 990) {
      this.hideMobileSidebar();
    } else {
      this.isSidebarVisible = true;
    }
  }

  onMenuToggleButtonClicked() {
    this.isSidebarPinned = !this.isSidebarPinned;

    // await Task.delay(250);

    document.body.classList.toggle("menu-pin");

    if (!this.isSidebarPinned) {
    }
    {
      this.onSidebarMouseLeave();
    }
  }

  onLogoClick() {
    const currentUser = this.authService.currentUserValue;
    if (currentUser) {
      let url;
      switch (currentUser.defaultPage) {
        case DefaultPageEnum.Dashboard:
          url = '/dashboard';
          break;
        case DefaultPageEnum.Scheduler:
          url = '/scheduling/scheduler';
          break;
      }
      if (!url) {
        url = '/dashboard';
      }
      this.router.navigate([url], { replaceUrl: true });
    }
  }

  onSidebarMouseEnter() {
    this.isSidebarOpen = true;
    document.body.classList.add("sidebar-visible");
  }

  onSidebarMouseLeave() {
    this.isSidebarOpen = this.isSidebarPinned;
    document.body.classList.remove("sidebar-visible");
  }

  async navigate(event, node: MenuMainNode) {
    event.preventDefault();

    if (this.openedMainNode == node) {
      this.isOpenedMainNodeCollapsed = !this.isOpenedMainNodeCollapsed;
      return;
    }

    const url = (
      node.children && node.children.length
        ? node.children.find((x) => x.isVisible === true)
        : node
    ).link;
    try {
      await this.navigationService.navigate(url);

      // const menuQuantityItems = node.children ?
      //   node.children.filter(item => item.quantityType != null).map(item => item.quantityType) :
      //   [];

      // if (menuQuantityItems.length) {
      //   this.menuQuantitiesService.updateQuantities(menuQuantityItems);
      // }
    } catch (e) {
      console.error(e);
    }
  }

  onLogoError() {
    this.isLogoError = true;
  }

  private showMobileSidebar() {
    this.isSidebarVisible = true;
    this.isSidebarOpen = true;
    this.isSidebarPinned = true;
    document.body.classList.add("sidebar-open");
    document.body.classList.add("menu-pin");
  }

  private hideMobileSidebar() {
    this.isSidebarVisible = false;
    this.isSidebarOpen = false;
    this.isSidebarPinned = false;
    document.body.classList.remove("sidebar-open");
    document.body.classList.remove("menu-pin");
  }

  private setMenuQuantities(quantityViewModels: MenuQuantityItemViewModel[]) {
    this.menuQuantities = this.menuQuantities || {};

    for (var quantityViewModel of quantityViewModels) {
      this.menuQuantities[quantityViewModel.menuQuantityItem] =
        quantityViewModel.quantity;
    }
  }
  private setWorkQueueMenuQuantities(quantityViewModels: MenuWorkQueueSubMenuViewModel[]) {
      this.workQueueMenuQuantities = this.workQueueMenuQuantities || {};

      for (var quantityViewModel of quantityViewModels) {
        this.workQueueMenuQuantities[quantityViewModel.id] =
          quantityViewModel.uncompletedNotesCount;
      }
  }


 
  private onNavigationEnd() {
    const currentPath = document.location.pathname;
    const openedMainNode = this.menuNodes.find(
     (x) =>
       currentPath == x.rootPath || currentPath.startsWith(x.rootPath + "/")
    );
    if (openedMainNode && openedMainNode.children && ['/patients', '/sales-orders'].includes(openedMainNode.rootPath)) {
      
      this.subMenuSelection(openedMainNode, currentPath);
    }
    
    this.userClinicService.openedMainNode = openedMainNode;
    const needToUpdateQuantities =
      openedMainNode && openedMainNode !== this.openedMainNode;
    this.openedMainNode = openedMainNode;
    this.isOpenedMainNodeCollapsed = false;

    if (needToUpdateQuantities) {
      const menuQuantityItems = this.openedMainNode.children
        ? this.openedMainNode.children
          .filter((item) => item.quantityType != null)
          .map((item) => item.quantityType)
        : [];
        const currentUser = this.authService.currentUserValue;
        if (currentUser && ((openedMainNode && openedMainNode.areaPermissionKey == this.workQueueAreaPermissionKeyName) || !this.openedFirstMainNode)) {
          this.menuQuantitiesService.updateWorkQueueSubMenu();
          this.openedFirstMainNode=this.openedMainNode;
        }
      if (menuQuantityItems.length) {
        this.menuQuantitiesService.updateQuantities(menuQuantityItems);
      }
    }
  }
  private subMenuSelection(openedMainNode: MenuMainNode, currentPath:string) {
      openedMainNode.children.forEach(child => child.isSelected = false);
      const matchingSubmenu = openedMainNode.children.find(
        child => child.link === openedMainNode.rootPath && child.isSubmenuPath
      );
      let navigatingSubmenu = openedMainNode.children.find(
        child => currentPath === child.link
      );
      if (!navigatingSubmenu) {
        navigatingSubmenu = openedMainNode.children.find(
          (child) => {
            const childLinkParts = child.link.split('/').slice(0, 3).join('/');
            const currentPathParts = currentPath.split('/').slice(0, 3).join('/');
            return currentPathParts === childLinkParts;
          }
        )
      }
      if (navigatingSubmenu) {
        navigatingSubmenu.isSelected = true;
      } else if (matchingSubmenu) {
        matchingSubmenu.isSelected = true;
      }

      if (matchingSubmenu && navigatingSubmenu && matchingSubmenu !== navigatingSubmenu) {
        matchingSubmenu.isSelected = false;
      }
      this.openedMainNode = openedMainNode;
  }

  private applyPermissions(accessSettings: RoleAccessSettings) {
if (!accessSettings) {
      this.hasMenuPermissions = false;
      return;
    }

    this.menuNodes.forEach((menuNode) => {
      if (menuNode.ignorePermissions === true) {
        menuNode.isVisible = true;
        return;
      }

      const area = accessSettings.entities[menuNode.areaPermissionKey];
      if (!area) {
        menuNode.isVisible = false;
      } else if (menuNode.children) {
        let isMenuNodeVisible = false;
        menuNode.children.forEach((childNode) => {
          const entity: EntityPermissionNode =
            area[childNode.entityPermissionKey];
          if (
            (entity && entity.canRead === true) ||
            childNode.ignorePermissions
          ) {
            childNode.isVisible = true;
            isMenuNodeVisible = true;
            if(area.title == this.workQueueKeyTitle){
              if(!area.workQueues.canEdit && !area.workQueues.canRead){
                childNode.isVisible = false;
                isMenuNodeVisible = false;
              }
            }
          } else {
            childNode.isVisible = false;
          }

         // Special
          if (this.sysPref.erpSystem == ErpSystemEnum.BusinessCentral) {
            if (childNode.customKey === MenuCustomKeys.inventoryOrdersKey) {
              childNode.title = "Menu.Inventory.InventoryOrders.Name";
            }

            //if (childNode.customKey === MenuCustomKeys.returnItemsKey) {
            //  childNode.link = "/inventory/inventory-returns";
            //}
          }

          if (childNode.customKey === MenuCustomKeys.eligibilitySearchKey) {
            childNode.isVisible = this.sysPref.useEligibilityAPI;
          }
        });

        menuNode.isVisible = isMenuNodeVisible;
      } else {
        menuNode.isVisible = this.permissionService.checkAreaAccess(
          menuNode.areaPermissionKey
        );
      }
    });

    this.hasMenuPermissions = true;
  }
}
